import React, { useEffect } from 'react';
import { useComponentsPool } from '../../../../ComponentsPool';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal } from '@material-ui/core';
import { isMobile } from 'react-device-detect';
import IconButton from '@material-ui/core/IconButton';
import { useModal } from '../../../modal/ModalContext';
import { buildUrlWithParams, getWosOrigin } from '../../../core/helper';
import { notification } from '../../../notification/actions';
import { getTickets } from '../../actions';
import useWosCredentials from '../../../authentication/useWosCredentials';


const WOS_ORIGIN = getWosOrigin();

// wos ticket creation page, used by this popup and by the QR code page
// path is the wos page to show; hasMultipleModes and onOpenQrCodeHome are only given by the QR code page
export const TicketCreationIframe = ({
  path = 'create-ticket',
  hasMultipleModes = false,
  onOpenQrCodeHome = null,
  onTicketCreated = null,
  onTicketCreationFailed = null,
  onCloseTicketCreation = null
}) => {
  const iframeRef = React.useRef(null);
  const wosUrl = buildUrlWithParams(`${WOS_ORIGIN}/${path}`, {
    parentOrigin: window.location.origin,
    hasMultipleModes: hasMultipleModes ? 'true' : null
  });
  useWosCredentials(iframeRef, wosUrl);

  useEffect(() => {
    if (!onOpenQrCodeHome && !onTicketCreated && !onTicketCreationFailed && !onCloseTicketCreation) {
      return undefined;
    }
    // A Map, so that a type like 'toString' can't match an Object prototype method
    const callbacksByMessageType = new Map([
      ['openQrCodeHome', onOpenQrCodeHome],
      ['ticketCreated', onTicketCreated],
      ['ticketCreationFailed', onTicketCreationFailed],
      ['closeTicketCreation', onCloseTicketCreation]
    ]);
    const handleMessage = event => {
      // Only from the exact wos origin
      const callback = event.origin === WOS_ORIGIN && callbacksByMessageType.get(event.data?.type);
      if (callback) {
        callback();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onOpenQrCodeHome, onTicketCreated, onTicketCreationFailed, onCloseTicketCreation]);

  return (
    <iframe
      ref={iframeRef}
      frameBorder="0"
      height="100%"
      width="100%"
      src={wosUrl}
      title="Ticket creation"
    ></iframe>
  );
};

const TicketCreatePopupV2 = () => {
  const { Component } = useComponentsPool();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const modal = useModal();

  const handleTicketCreated = () => {
    modal.close();
    dispatch(notification(t('notifications:create_ticket'), 'successful', 'check'));
    // wos writes the ticket straight into the database, so no websocket event refreshes the list
    dispatch(getTickets());
  };

  const handleTicketCreationFailed = () => {
    dispatch(notification(t('notifications:create_ticket_error'), 'error', 'frown'));
  };

  const ticketCreationIframe = (
    <TicketCreationIframe
      onTicketCreated={handleTicketCreated}
      onTicketCreationFailed={handleTicketCreationFailed}
      onCloseTicketCreation={modal.close}
    />
  );

  if (isMobile) {
    return (
      <Component classNames={['ticket-create-popup-v2-mobile-wrapper']} componentName={'Modal'}
        content={
          <div className="ticket-create-popup-v2-mobile">
            {ticketCreationIframe}
          </div>
        }
      />
    );
  }
  return (
    <Modal open onClose={modal.close}>
      <div className={'ticket-create-popup-v2'}>
        <div className="header">
          <IconButton className="close-button" onClick={modal.close}>
            <Component componentName={'Icon'} type={'close'} />
          </IconButton>
        </div>
        <div className="content">
          {ticketCreationIframe}
        </div>
      </div>
    </Modal>
  );
};

export default TicketCreatePopupV2;
